import { StarFilledIcon, Typography } from '@iheartjane/reefer';
import { useId, useState } from 'react';

import styles from './starRatingField.module.css';

export interface StarRatingFieldProps {
  /** Horizontal alignment of the legend, stars and rating label. Defaults to "left". */
  align?: 'left' | 'center';
  /** Group label. Defaults to "Overall product rating". */
  legend?: string;
  /** Fired with the selected whole-star rating (1-5). */
  onChange: (rating: number) => void;
  /** `data-testid` on the wrapper. */
  testId?: string;
  /** Shows a label below the stars describing the hovered or selected rating. */
  showRatingLabel?: boolean;
  /** Current rating. 0 means nothing is selected. */
  value: number;
}

const STAR_VALUES = [1, 2, 3, 4, 5];

const RATING_LABELS: Record<number, string> = {
  1: 'Not for me',
  2: "It's alright",
  3: 'Does the job',
  4: 'Pretty good!',
  5: 'Amazing!',
};

export function StarRatingField({
  align = 'left',
  legend = 'Overall product rating',
  onChange,
  showRatingLabel = false,
  testId = 'star-rating-field',
  value,
}: StarRatingFieldProps) {
  const [previewValue, setPreviewValue] = useState(0);
  const legendId = useId();
  const radioGroupName = useId();

  const displayValue = previewValue || value;

  return (
    <fieldset
      aria-labelledby={legendId}
      className={align === 'center' ? styles['centered'] : undefined}
      data-testid={testId}
      role="radiogroup"
    >
      <Typography
        as="legend"
        className={styles['legend']}
        id={legendId}
        textAlign={align}
        variant="body-semibold"
      >
        {legend}
      </Typography>
      <div className={styles['stars']} onMouseLeave={() => setPreviewValue(0)}>
        {STAR_VALUES.map((starValue) => (
          <label
            key={starValue}
            className={styles['star']}
            onMouseEnter={() => setPreviewValue(starValue)}
          >
            <input
              aria-label={`${starValue} star${starValue === 1 ? '' : 's'}, ${RATING_LABELS[starValue]}`}
              checked={value === starValue}
              className={styles['input']}
              name={radioGroupName}
              onChange={() => onChange(starValue)}
              type="radio"
              value={starValue}
            />
            <StarFilledIcon
              className={
                starValue <= displayValue
                  ? styles['starFilled']
                  : styles['starEmpty']
              }
              data-testid={
                starValue <= displayValue
                  ? `${testId}-star-filled`
                  : `${testId}-star-empty`
              }
              size="lg"
            />
          </label>
        ))}
      </div>
      {showRatingLabel && (
        <Typography
          aria-hidden
          color="grays-mid"
          mt={8}
          textAlign={align}
          variant="small-body-regular"
        >
          {RATING_LABELS[displayValue] ?? 'Tap a star to rate'}
        </Typography>
      )}
    </fieldset>
  );
}
